<template>
    <main class="p-5 bg-echoes-background-black">
        <!-- Header Section -->
        <UsersHeader>
            <template #title>Tag Types Management</template>
            <template #description>
                Manage and organize your tag types from a central dashboard.
            </template>
            <template #actions>
                <div class="flex items-center gap-2">
                    <button @click="createTagType" class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200">
                        <i class="mdi mdi-plus mr-2"></i> New Tag Type
                    </button>
                    <button @click="showTopIndustryModal = true" class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200">
                        Set Top Industries
                    </button>
                </div>
                <TopIndustryModal :show="showTopIndustryModal" @close="showTopIndustryModal = false" v-if="showTopIndustryModal" />
            </template>
        </UsersHeader>

        <!-- Stats Cards -->
        <div class="mt-6 grid grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="uil uil-layer-group text-2xl text-white"></i>
                </template>
                <template #title>Total Tag Types</template>
                <template #value>{{ rowData.length }}</template>
            </StatCard>
        </div>

        <!-- Main Content -->
        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search tag types..." 
                                class="w-full"
                            />
                        </div>
                        <div class="flex items-center gap-4">
                            <div class="w-full sm:w-24">
                                <SelectInput
                                    v-model="itemsPerPage"
                                    :options="itemsPerPageOptions"
                                    :hide-search="true"
                                    @update:modelValue="currentPage = 1"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div>
                    <div v-if="rowData.length === 0" class="h-[40vh]">
                        <div class="flex items-center justify-center h-full">
                            <div class="text-white/80 text-sm">No tag types found</div>
                        </div>
                    </div>
                    <table v-else class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Tag Type
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Description
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Is Eval Type
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Is Annotation Type
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="tagType in paginatedTagTypes" :key="tagType.id">
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ tagType.type }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm text-white/80 max-w-md truncate" :title="tagType.description_tooltip">
                                            {{ tagType.description }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm text-white/80">
                                            {{ tagType.is_eval ? 'Yes' : 'No' }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm text-white/80">
                                            {{ tagType.is_annotation ? 'Yes' : 'No' }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center space-x-4">
                                            <button 
                                                @click="editTagType(tagType)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                                title="Edit Tag Type"
                                            >
                                            <i class="mdi mdi-pencil mr-2"></i>
                                            Edit
                                            </button>
                                            <button 
                                                @click="deleteTagType(tagType)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                                title="Delete Tag Type"
                                            >
                                            <i class="mdi mdi-trash-can-outline mr-2"></i>
                                            Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredTagTypes.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredTagTypes.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="currentPage = page"
                                            :class="[
                                                'px-3 py-1 rounded-md text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create/Edit Tag Type Modal -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
                
                <div class="relative bg-echoes-background-gray border border-echoes-border-light rounded-xl max-w-md w-full">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                                {{ isEditing ? 'Edit Tag Type' : 'New Tag Type' }}
                            </h2>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {{ isEditing ? 'Update tag type information' : 'Create a new tag type' }}
                            </p>
                        </div>
                        <button 
                            @click="closeModal" 
                            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                            <span class="sr-only">Close</span>
                            <svg class="p-2 w-10 h-10 rounded-lg text-white/80 hover:text-red-500/80 bg-transparent hover:bg-white/5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form @submit.prevent="handleSubmit" class="p-6 space-y-6">
                        <div>
                            <label for="tagType" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tag Type Name
                            </label>
                            <TextInput id="tagType" v-model="newTagType" placeholder="Enter tag type name" />
                        </div>

                        <div>
                            <label for="tagTypeDescription" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description
                            </label>
                            <TextInput v-model="newTagTypeDescription" placeholder="Enter tag type description" type="textarea" rows="3" />
                        </div>

                        <div class="flex justify-between gap-3 mt-6">
                            <div class="flex items-center gap-2">
                                <label for="tagTypeDescription" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Is Eval Type
                                </label>
                                <Checkbox v-model="isEvalType" :checked="isEvalType" />
                            </div>
                            <div class="flex items-center gap-2">
                                <label for="tagTypeDescription" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Is Annotation Type
                                </label>
                                <Checkbox v-model="isAnnotationType" :checked="isAnnotationType" />
                            </div>
                            <div>
                                <button 
                                    type="button"
                                    @click="closeModal"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-echoes-primary hover:bg-echoes-primary/90 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-echoes-primary"
                                >
                                    {{ isEditing ? 'Save Changes' : 'Create Tag Type' }}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import { useLayoutComponents } from '@/composables/layout.js';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import TopIndustryModal from '@/pages/admin/leaderboard/components/topIndustryModal.vue';

const tagTypesOptions = ref([]);
const showModal = ref(false);
const newTagType = ref('');
const newTagTypeDescription = ref('');
const rowData = ref([]);
const searchQuery = ref('');
const isEditing = ref(false);
const editingTagId = ref(null);
const isCustomType = ref(false);
const isEvalType = ref(false);
const isAnnotationType = ref(false);
const showTopIndustryModal = ref(false);

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const { addToast } = useToast();
const { TextInput, SelectInput, Checkbox } = useLayoutComponents();

// Computed properties for filtering and pagination
const filteredTagTypes = computed(() => {
    let filtered = rowData.value;
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = rowData.value.filter(tagType => 
            tagType.type.toLowerCase().includes(query) ||
            tagType.description.toLowerCase().includes(query)
        );
    }
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredTagTypes.value.length / itemsPerPage.value);
});

const paginatedTagTypes = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredTagTypes.value.slice(start, end);
});

onMounted(async () => {
    await fetchTagTypes();
    await fetchTagTypesOptions();
});

const fetchTagTypes = async () => {
    try {
        const response = await axios.get('/api/tag_types');
        if(response.status === 200) {
            console.log(response.data);
            rowData.value = response.data.map(tagType => {
                let truncatedDescription;
                if (tagType.description) {
                    if (tagType.description.length > 100) {
                        truncatedDescription = tagType.description.substring(0, 100) + '...';
                    } else {
                        truncatedDescription = tagType.description;
                    }
                } else {
                    truncatedDescription = 'No description';
                }
                
                return {
                    id: tagType.id,
                    type: tagType.type,
                    description: truncatedDescription,
                    description_tooltip: tagType.description || 'No description',
                    is_eval: tagType.is_eval,
                    is_annotation: tagType.is_annotation,
                };
            });
        } else {
            console.error('Error fetching tag types:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tag types:', error);
    }
};

const fetchTagTypesOptions = async () => {
    try {
        const response = await axios.get('/api/tag_types');
        if(response.status === 200) {
            tagTypesOptions.value = response.data.map(tagTypes => ({
                label: tagTypes.type,
                value: tagTypes.type
            }));
        } else {
            console.error('Error fetching tag types:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tag types:', error);
    }
};

const createTagType = () => {
    isEditing.value = false;
    newTagType.value = '';
    newTagTypeDescription.value = '';
    showModal.value = true;
    isEvalType.value = false;
    isAnnotationType.value = false;
};

const editTagType = async (tagType) => {
    isEditing.value = true;
    editingTagId.value = tagType.id;
    newTagType.value = tagType.type;
    newTagTypeDescription.value = tagType.description_tooltip;
    showModal.value = true;
    isEvalType.value = tagType.is_eval;
    isAnnotationType.value = tagType.is_annotation;
};

const deleteTagType = async (tagType) => {
    try {
        if(confirm('Are you sure you want to delete this tag type?') && tagType?.id) {
            const response = await axios.post(`/api/tag_types/${tagType.id}/delete`);
            if(response.status === 200 || response.status === 201) {
                await fetchTagTypes();
                addToast('Tag Type Deleted','success');
            } else {
                addToast(response.data.message || 'An error occurred','error');
            }
        }
    } catch (error) {
        addToast(error.response?.data?.message || 'An error occurred','error');
    }
};

const closeModal = () => {
    showModal.value = false;
    newTagType.value = '';
    newTagTypeDescription.value = '';
    isEditing.value = false;
    editingTagId.value = null;
    isCustomType.value = false;
    isEvalType.value = false;
    isAnnotationType.value = false;
};

const handleSubmit = async () => {
    const url = isEditing.value ? `/api/tag_types/${editingTagId.value}` : '/api/tag_types';
    const payload = {
        type: newTagType.value,
        description: newTagTypeDescription.value,
        is_eval: isEvalType.value,
        is_annotation: isAnnotationType.value,
    };

    try {
        const response = await axios.post(url, payload);
        if (response.status === 201) {
            await fetchTagTypes();
            closeModal();
        } else {
            console.error('Error creating/updating tag type:', response.data);
        }
    } catch (error) {
        console.error('Error creating/updating tag type:', error);
    }
    closeModal();
};
</script>

<style scoped lang="postcss">
/* Custom Scrollbar */
::-webkit-scrollbar {
    @apply w-2;
}

::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

/* Stats Cards Hover Effect */
.bg-white.dark\:bg-slate-700 {
    @apply transition-all duration-200 hover:shadow-lg hover:scale-[1.02];
}
</style>